import { AppType } from "client/Api/Apps/common";
import { ReactElement, useCallback } from "react";
import { useRenderManager } from "../../useRenderManager";
import { EmailLiveInformationProvider } from "client_legacy/Workspace/LiveInformation/Email/provider";
import { OneByOneTileContent } from "../OneByOneTileContent";
import { TwoByTwoTileContent } from "../TwoByTwoTileContent";
import { EmailLiveTileContent, EmailTileNavigationOptions } from "./EmailLiveTileContent";
import { TileRenderState } from "../../RenderState/common";
import { useBaseNavigation } from "../useInvokeNavigation";
import { navigateToEmailApp } from "client_legacy/Apps/appNavigation";
import { useRegisterTileUsage } from "../useRegisterTileUsage";
import { TileContextProvider } from "../../TileContextProvider";
import { Coordinate } from "client/Misc/commonTypes";

export type EmailTileContentProps = {
	renderState: TileRenderState<AppType.Email>;
	onEdit: (() => void) | undefined;
	onOpenTileMenu: (coordinate: Coordinate) => void;
	provider: EmailLiveInformationProvider;
};

export const EmailTileContent = (props: EmailTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		onOpenTileMenu,
		provider
	} = props;

	const [navigationElement, navigateHandler] = useEmailTileNavigation(
		renderState,
		onEdit
	);

	const {
		dimension,
		data: { settings }
	} = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<EmailLiveTileContent
				destinationApp={settings.destinationApp}
				destinationWindow={settings.destinationWindow}
				tabs={settings.tabs}
				provider={provider}
				onNavigate={navigateHandler}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		],
		[
			{ columnSpan: 2, rowSpan: 2 },
			() => (
				<TwoByTwoTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
EmailTileContent.displayName = "EmailTileContent";

export function useEmailTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.Email>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined
): [
	element: ReactElement | null,
	(options?: EmailTileNavigationOptions) => void | Promise<void>
] {
	const {
		settings: {
			destinationApp,
			destinationWindow
		}
	} = renderStateIsh.data;

	const registerTileUsage: () => void = useRegisterTileUsage(renderStateIsh);

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useCallback(
			(options: EmailTileNavigationOptions = {}): void => {
				navigateToEmailApp(
					{
						destinationApp,
						destinationWindow
					},
					options,
					() => registerTileUsage()
				);
			},
			[
				registerTileUsage,
				destinationApp,
				destinationWindow
			]
		)
	);
}
